<template>
  <section
    id="tech"
    class="padding-top-5xl"
  >
    <div class="row margin-bottom-4xl">
      <div class="column-12 text-center">
        <h2
          class="text-uppercase size-md with-anchor"
          style="letter-spacing: var(--letter-spacing-3xl); font-weight: var(--font-weight-black); color: var(--fa-md-gravy)"
        >
          <anchor-link
            anchor="#tech"
            link-color="gravy"
            anchor-name="Tech Updates"
          />
          Only the Freshest Ingredients
        </h2>
        <h2 class="margin-y-md">Tech Updates</h2>
        <p class="line-length-xl margin-auto">
          With Font Awesome 7, we've made huge improvements to how icons work behind the scenes. From minor tweaks to major overhauls, our toolkit is now faster
          than ever.
        </p>
      </div>
    </div>
    <div
      class="row"
      style="--card-vertical-padding: var(--spacing-2xl)"
    >
      <div class="column-6 laptop:column-3">
        <div class="card with-top-tag header">
          <span class="tag top purple">New!</span>
          <div class="header">
            <font-awesome-icon
              :icon="$v7.solid.faGaugeMax"
              size="2x"
            />
          </div>
          <div class="text-left">
            <h3 class="h5">Faster Load Times!</h3>
            <p>Refined SVG/JS, optimized font files, and compressed CSS.</p>
          </div>
        </div>
      </div>
      <div class="column-6 laptop:column-3">
        <div class="card with-top-tag">
          <span class="tag top teal">New!</span>
          <div class="inverted header">
            <font-awesome-icon
              :icon="$v7.solid.faArrowRotateRight"
              size="2x"
            />
          </div>
          <div class="text-left">
            <h3 class="h5">Toolkit Updates!</h3>
            <p>Upgraded to Dart Sass with modern SCSS and improved CSS properties.</p>
          </div>
        </div>
      </div>
      <div class="column-6 laptop:column-3">
        <div class="card with-top-tag header">
          <span class="tag top purple">New!</span>
          <div class="header">
            <font-awesome-icon
              :icon="$v7.solid.faArrowsLeftRightToLine"
              size="2x"
            />
          </div>
          <div class="text-left">
            <h3 class="h5">Consistent Icon Widths!</h3>
            <p>Icon canvases now render at a fixed width, with new "automatic" option.</p>
          </div>
        </div>
      </div>
      <div class="column-6 laptop:column-3">
        <div class="card with-top-tag">
          <span class="tag top teal">New!</span>
          <div class="inverted header">
            <font-awesome-icon
              :icon="$v7.solid.faUniversalAccess"
              size="2x"
            />
          </div>
          <div class="text-left">
            <h3 class="h5">Improved Accessibility!</h3>
            <p>Better screen reader support, assistive tech, and RTL compatibility.</p>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="column-12 laptop:column-4 display-flex flex-column align-stretch flex-content-between">
        <div class="card roomy blue tablet:display-flex laptop:display-block">
          <div
            style="flex: 1"
            class="display-flex align-middle align-center flex-column"
          >
            <img
              src="https://img.fortawesome.com/1ce05b4b/badge-code.svg"
              alt="decorative code badge"
              class="margin-auto padding-md"
            />
            <h3
              class="h4 margin-y-xl"
              style="color: var(--fa-navy)"
            >
              Great for Developers
            </h3>
          </div>
          <div style="flex: 1">
            <site-link
              :to="$docs('/apis/javascript')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_api_get_started_link_click')"
            >
              <font-awesome-icon
                :icon="$v7.solid.faPuzzle"
                fixed-widt
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-blue)"
              />

              <span>Components for JS Frameworks</span>
            </site-link>
            <site-link
              :to="$docs('/web/setup/use-kit')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_use_kit_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faGlobe"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-blue)"
              />
              <span>Hosted CDN Options</span>
            </site-link>
            <site-link
              :to="$docs('/web/setup/packages')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_packages_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faBox"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-blue)"
              />
              <span>Install via Package Managers</span>
            </site-link>
            <site-link
              :to="$docs('/apis/graphql')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_apis_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faPlug"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-blue)"
              />
              <span>Official GraphQL API</span>
            </site-link>
            <site-link
              :to="$docs('/web')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_web_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faBracketsCurly"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-blue)"
              />
              <span>CSS Framework</span>
            </site-link>
          </div>
        </div>
      </div>
      <div class="column-12 laptop:column-4 display-flex flex-column align-stretch flex-content-between">
        <div class="card roomy violet tablet:display-flex laptop:display-block">
          <div
            style="flex: 1"
            class="display-flex align-middle align-center flex-column"
          >
            <img
              src="https://img.fortawesome.com/1ce05b4b/badge-pen-nib.svg"
              alt="decorative pen nib badge"
              class="margin-auto padding-md"
            />
            <h3
              class="h4 margin-y-xl"
              style="color: var(--fa-navy)"
            >
              Great for Designers
            </h3>
          </div>
          <div style="flex: 1">
            <site-link
              :to="$search()"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:icon_search_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faIcons"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-violet)"
              />
              <span>Thousands of Icons</span>
            </site-link>
            <site-link
              :to="$docs('/web/add-icons/upload-icons')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_upload_icons_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faCloudUpload"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-violet)"
              />
              <span>Upload Your Own Icons</span>
            </site-link>
            <site-link
              :to="$docs('/web/setup/use-kit')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_use_kit_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faSuitcase"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-violet)"
              />
              <span>Easy Development Hand-off</span>
            </site-link>
            <site-link
              :to="$docs('/desktop/add-icons/ligatures')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_ligatures_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faTextSize"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-violet)"
              />
              <span>Text-to-Icon Ligatures</span>
            </site-link>

            <a
              href="https://www.figma.com/community/file/1529485083934388018/font-awesome-7-icon-component"
              target="_blank"
              class="display-flex align-middle padding-y-sm"
              @click="$posthog.capture('home:figma_component_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.brands.faFigma"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-violet)"
              />
              <span>Figma Component</span>
            </a>
          </div>
        </div>
      </div>

      <div class="column-12 laptop:column-4 display-flex flex-column align-stretch flex-content-between">
        <div class="card roomy purple tablet:display-flex laptop:display-block">
          <div
            style="flex: 1"
            class="display-flex align-middle align-center flex-column"
          >
            <img
              src="https://img.fortawesome.com/1ce05b4b/badge-file.svg"
              alt="decorative content badge"
              class="margin-auto padding-md"
            />

            <h3
              class="h4 margin-y-xl"
              style="color: var(--fa-navy)"
            >
              Great for Content Creators
            </h3>
          </div>
          <div style="flex: 1">
            <site-link
              :to="$docs('/web/setup/use-kit')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_use_kit_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faGlobe"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-purple)"
              />
              <span>Easy Install + Hosting</span>
            </site-link>
            <site-link
              :to="$docs('/web/style/styling')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_styling_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faPalette"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-purple)"
              />
              <span>Styling Shortcuts</span>
            </site-link>
            <site-link
              :to="$docs('/web/use-with/wordpress')"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:docs_wordpress_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faHashtag"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-purple)"
              />
              <span>Wordpress Plugin</span>
            </site-link>
            <site-link
              :to="$search({ iconCollections: ['free'] })"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:free_icon_search_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.solid.faHeart"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-purple)"
              />
              <span>Open Source Options</span>
            </site-link>
            <site-link
              :to="$search({ iconCollections: ['brands'] })"
              class="display-flex align-middle padding-y-sm"
              @click.native="$posthog.capture('home:brands_icon_search_link_click', { location: 'tools' })"
            >
              <font-awesome-icon
                :icon="$v7.brands.faFontAwesome"
                size="lg"
                class="padding-right-sm"
                style="color: var(--fa-dk-purple)"
              />
              <span>Popular Brand Icons</span>
            </site-link>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import AnchorLink from '../components/AnchorLink'

  export default {
    name: 'Tech',

    components: {
      AnchorLink
    },

    props: {
      isSignedIn: {
        type: Boolean,
        default: false
      }
    }
  }
</script>
